import { isEmpty, isEqual, isNil, uniqWith } from 'lodash';
import moment from 'moment';
import { getFilterComponent } from '@shared/data/cards/CardsEntityService';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';
import { uuid as getUuid } from '@shared/util/uuid';
import {
  ApiFilterByComponent,
  ControlsState,
  EMPTY_RELATED_FILTER_OPTIONS,
  FilterOption,
  FilterSelection,
  FilterSelectionState,
  NumberOperator,
  RelatedFilterOptions,
  SortOption,
  SortSelectionState,
} from '../types';
import { getFilterScalarKey, getFilterValueType } from './filterScalarKey';
import { unwrapRelatedEntityFilter } from './getRelatedEntityFilterPaths';
import { getRelatedEntityPathKey } from './relatedEntityPath';

export function mapCollectionCriteriaToState(
  collectionCriteria: API.CollectionCriteriaFragment | null,
  availableFilters: FilterOption[],
  availableSorts: SortOption[],
  availableGroups: FilterOption[],
  availableRelatedFilters: RelatedFilterOptions = EMPTY_RELATED_FILTER_OPTIONS,
  availableRelatedGroups: RelatedFilterOptions = EMPTY_RELATED_FILTER_OPTIONS,
): ControlsState {
  // TODO
  // omniSearchQuery if saved

  const filters = mapFilterByComponentToState(
    collectionCriteria?.filterByComponents ?? [],
    availableFilters,
    availableRelatedFilters,
  );

  const sorts = mapSortByComponentToState(
    collectionCriteria?.sortByComponents ?? [],
    availableSorts,
  );

  const groups = mapGroupsToState(
    collectionCriteria?.groups ?? [],
    availableGroups,
    availableRelatedGroups,
  );

  return {
    filters,
    availableFilters,
    selectedFilters: Array.from(filters.values())
      .map(filter => filter.filter)
      .filter(notNil),

    sorts,
    availableSorts,
    selectedSorts: Array.from(sorts.values())
      .map(sort => sort.sort)
      .filter(notNil),

    groups,
    availableGroups,
    selectedGroups: groups.map(g =>
      Array.from(g.values())
        .map(group => group.filter)
        .filter(notNil),
    ),

    availableRelatedFilters,
    availableRelatedGroups,

    search: null,
  };
}

export function mapFilterByComponentToState(
  filterByComponents: ApiFilterByComponent[],
  availableFilters: FilterOption[],
  availableRelatedFilters: RelatedFilterOptions = EMPTY_RELATED_FILTER_OPTIONS,
): Map<string, FilterSelectionState> {
  const filters = new Map<string, FilterSelectionState>();
  filterByComponents?.forEach(savedFilter => {
    // A cross-workflow filter names a related-card component at every level above the field it
    // actually filters on, so decode the path first and the field at the end of it against that
    // path's nested options. An absent entry means that path isn't resolved yet, in which case the
    // filter is dropped for this pass (same as an archived component) and picked up on the next
    // one. A path deeper than the fragment can read back comes back truncated - the deepest hop
    // the fragment did reach looks like the filter, with no value on it - and is dropped, because
    // the field it really points at was never fetched.
    const { componentIds, leaf: filter, truncated } = unwrapRelatedEntityFilter(savedFilter);
    if (truncated) {
      return;
    }
    const options = isEmpty(componentIds)
      ? availableFilters
      : (availableRelatedFilters.get(getRelatedEntityPathKey(componentIds)) ?? []);
    const option = findFilterOption(filter, options);
    if (!isNil(option) && !isNil(option.component)) {
      const uuid = option.uuid ?? getUuid();
      let selection: FilterSelection | null = null;
      let negate = false;
      const match = filter.value.containsCurrentWorkflowTemplateComponentValue;
      if (!isNil(match)) {
        // A row that defers to a field on the card being edited. Which branch to read doesn't
        // depend on the component's type here - the value names a field rather than holding one -
        // so this is decided before the per-type switch below rather than inside it.
        filters.set(uuid, {
          filter: { uuid, ...option },
          selection: { type: 'COMPONENT_MATCH', componentId: match.componentId },
          negate: false,
        });
        return;
      }
      // The component's own type, unless a scalar representation reads as something else - see
      // `getFilterValueType`. Which `FilterOneOf` branch to read follows the value that was
      // compared, mirroring how `getComponentFilterValue` chose which one to write. Dispatching on
      // the component instead would decode the wrong branch for every representation that doesn't
      // read as its own component type.
      switch (getFilterValueType(option)) {
        case 'BOOLEAN': {
          selection = isNil(filter.value.booleanIs)
            ? null
            : { type: 'BOOLEAN', value: filter.value.booleanIs.value };
          break;
        }
        case 'TAG': {
          if (!isNil(filter.value.containsString?.values)) {
            selection = filter.value.containsString.values.map(value => ({
              type: 'TAG',
              filter: { uuid, ...option },
              value,
            }));
            negate = !!filter.value.containsString.negate;
          } else {
            selection = [
              { type: 'TAG', filter: { uuid, ...option }, value: getEmptyOperator(filter) },
            ];
          }
          break;
        }
        case 'RELATED_CARD': {
          if (!isNil(filter.value.containsString?.values)) {
            selection = filter.value.containsString.values.map(value => ({
              type: 'RELATED_CARD',
              filter: { uuid, ...option },
              value,
            }));
          } else {
            selection = [
              {
                type: 'RELATED_CARD',
                filter: { uuid, ...option },
                value: getEmptyOperator(filter),
              },
            ];
          }
          break;
        }
        case 'PERSON': {
          const personFilter = { uuid, ...option };
          if (!isNil(filter.value.containsNumber?.values)) {
            selection = filter.value.containsNumber.values.map(value => ({
              type: 'PERSON',
              filter: personFilter,
              value,
            }));
          } else if (!isNil(filter.value.containsCurrentUser)) {
            selection = [{ type: 'PERSON', filter: personFilter, value: 'currentUser' }];
          } else {
            selection = [{ type: 'PERSON', filter: personFilter, value: getEmptyOperator(filter) }];
          }
          break;
        }
        case 'TEXT': {
          selection = filter.value.isEmpty
            ? { type: 'TEXT', operator: getEmptyOperator(filter) }
            : { type: 'TEXT', operator: 'contains', value: filter.value.stringWildcard?.value };
          break;
        }
        case 'DATE': {
          if (filter.value.isEmpty) {
            selection = { type: 'DATE', operator: getEmptyOperator(filter) };
          } else if (!isNil(filter.value.dateIs)) {
            // A single relative bound maps onto the relative `startsOn`/`endsOn`
            // model: GT/GTE = "on or after" (start), LT/LTE = "on or before"
            // (end). This also migrates legacy daysBeforeToday/daysAfterToday
            // filters, which used GTE/LTE respectively.
            const relativeDate = filter.value.dateIs.relativeDate!;
            const operator = filter.value.dateIs.operator;
            const isStart = operator === 'GT' || operator === 'GTE';
            selection = {
              type: 'DATE',
              operator: isStart ? 'startsOn' : 'endsOn',
              comparisonOperator: operator,
              duration: relativeDate.duration,
              isRelative: true,
            };
          } else if (!isNil(filter.value.dateTokenIs)) {
            selection = { type: 'DATE', operator: 'today' };
          } else if (
            !isNil(filter.value.dateRange?.after) &&
            !isNil(filter.value.dateRange?.before) &&
            moment(filter.value.dateRange?.after).startOf('day').toISOString() ===
              moment(filter.value.dateRange?.before).startOf('day').toISOString()
          ) {
            selection = {
              type: 'DATE',
              operator: 'exactly',
              after: filter.value.dateRange?.after ?? undefined,
              before: filter.value.dateRange?.before ?? undefined,
            };
          } else if (
            !isNil(filter.value.dateRange?.after) &&
            isNil(filter.value.dateRange?.before)
          ) {
            selection = {
              type: 'DATE',
              operator: 'startsOn',
              after: filter.value.dateRange?.after ?? undefined,
            };
          } else if (
            isNil(filter.value.dateRange?.after) &&
            !isNil(filter.value.dateRange?.before)
          ) {
            selection = {
              type: 'DATE',
              operator: 'endsOn',
              before: filter.value.dateRange?.before ?? undefined,
            };
          } else {
            selection = {
              type: 'DATE',
              operator: 'between',
              after: filter.value.dateRange?.after ?? undefined,
              before: filter.value.dateRange?.before ?? undefined,
            };
          }
          break;
        }
        case 'NUMBER': {
          if (filter.value.isEmpty) {
            selection = { type: 'NUMBER', operator: getEmptyOperator(filter), value: 0 };
          } else if (filter.value.numberIs) {
            selection = {
              type: 'NUMBER',
              operator: filter.value.numberIs.operator as NumberOperator,
              value: filter.value.numberIs.value,
            };
          } else {
            selection = null;
          }
          break;
        }
      }

      if (selection) {
        filters.set(uuid, {
          filter: { uuid, ...option },
          selection,
          negate,
        });
      }
    }
  });

  return filters;
}

/**
 * Which way a saved `isEmpty` filter reads - its `value` is `false` for "is not empty". Anything
 * else, including no `isEmpty` at all, reads as "is empty", which is what every type's fallback
 * decoded to before "is not empty" existed.
 */
function getEmptyOperator(filter: API.FilterByComponentFragment): 'empty' | 'notEmpty' {
  return filter.value.isEmpty?.value === false ? 'notEmpty' : 'empty';
}

/**
 * The available option a saved filter refers to.
 *
 * One component can contribute several options - its own value plus a scalar representation of it
 * (see `toFilterOptions`) - so the component id alone doesn't identify one. Matching on the
 * resolved `scalarKey` picks the right sibling, and works for a component type whose key is
 * applied unconditionally too, since `getFilterScalarKey` fills that in for both sides.
 *
 * The fallback covers criteria saved before a component type had any scalar representation: the
 * saved filter carries no `scalarKey`, but the sole option it can mean now resolves to one. That
 * is unambiguous only while the component contributes exactly one option; past that, an
 * unrecognized key drops the filter rather than guessing (same as an archived component).
 */
function findFilterOption(
  filter: API.FilterByComponentFragment,
  availableFilters: FilterOption[],
): FilterOption | undefined {
  const candidates = availableFilters.filter(option => option.component?.id === filter.componentId);
  const scalarKey = filter.scalarKey ?? null;
  return (
    candidates.find(option => getFilterScalarKey(option) === scalarKey) ??
    (candidates.length === 1 ? candidates[0] : undefined)
  );
}

function mapSortByComponentToState(
  sortByComponents: API.SortByComponent[],
  availableSorts: SortOption[],
): Map<string, SortSelectionState> {
  const sorts = new Map<string, SortSelectionState>();
  sortByComponents?.forEach(sort => {
    const option = availableSorts.find(sortOption => sortOption.component?.id === sort.componentId);
    if (!isNil(option)) {
      const uuid = getUuid();
      const direction: API.SortDirection = sort.direction === 'ASC' ? 'ASC' : 'DESC';
      sorts.set(uuid, {
        sort: { uuid, ...option, direction },
        direction,
      });
    }
  });
  return sorts;
}

function mapGroupsToState(
  groups: API.CollectionGroup[],
  availableGroups: FilterOption[],
  availableRelatedGroups: RelatedFilterOptions = EMPTY_RELATED_FILTER_OPTIONS,
) {
  const groupMaps = groups.map(group => {
    const groupState = mapFilterByComponentToState(
      group.filterByComponents ?? [],
      availableGroups,
      availableRelatedGroups,
    );
    const groupMap = new Map<string, FilterSelectionState>();
    groupState.forEach(state => {
      if (!isNil(state.filter?.uuid)) {
        groupMap.set(state.filter?.uuid, state);
      }
    });
    return groupMap;
  });

  // The group menu won't add the same group twice, but a bundle or the API can. Duplicates
  // share a group id, which list keys, fetched cards and collapse state are all keyed by.
  return uniqWith(groupMaps, (a, b) =>
    isEqual(
      Array.from(a.values()).map(getFilterComponent),
      Array.from(b.values()).map(getFilterComponent),
    ),
  );
}
